/**
 * Integrytas Group - Common Security Module (CSM)
 * Telemetry — structured error capture and reporting.
 *
 * reportError() is called by:
 *   • ErrorBoundary (React component tree crashes)
 *   • Global window.onerror / unhandledrejection handlers (set up in App.tsx)
 *   • Any page that catches a non-recoverable error
 *
 * Every error is written to csm_telemetry_log via the csm_log_error() RPC.
 * The call is best-effort — a failure to log must NEVER propagate back to
 * the caller or mask the original error.
 */

import { apiDb } from './api_client';
import type { AuthSession } from '../types/auth';

export type TelemetryErrorType = 'FRONTEND' | 'BACKEND' | 'EDGE_FUNCTION' | 'UNHANDLED';
export type TelemetrySeverity  = 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

export interface TelemetryContext {
  errorType?:    TelemetryErrorType;
  severity?:     TelemetrySeverity;
  componentName?: string;
  pageCode?:     string;
  session?:      AuthSession | null;
  metadata?:     Record<string, unknown>;
}

let _session: AuthSession | null = null;

/** Call once from App when the session changes so reportError can stamp userId. */
export function setTelemetrySession(s: AuthSession | null) {
  _session = s;
}

export async function reportError(error: unknown, ctx: TelemetryContext = {}): Promise<void> {
  try {
    const err      = error instanceof Error ? error : new Error(String(error));
    const session  = ctx.session ?? _session;
    const userId   = session?.effective_user.user_id ?? null;
    const token    = session?.session_token ?? null;

    await apiDb.rpc('csm_log_error', {
      p_error_type:    ctx.errorType   ?? 'FRONTEND',
      p_severity:      ctx.severity    ?? 'ERROR',
      p_message:       err.message,
      p_stack_trace:   err.stack       ?? null,
      p_component:     ctx.componentName ?? null,
      p_page_code:     ctx.pageCode    ?? null,
      p_user_id:       userId,
      p_session_token: token,
      p_url:           window.location.href,
      p_user_agent:    navigator.userAgent,
      p_app_version:   (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null,
      p_metadata:      ctx.metadata ? JSON.stringify(ctx.metadata) : null,
    });
  } catch {
    // Swallow: logging must never throw
  }
}
